/**
 * OpenAI Speech Helpers
 * TTS validation and STT transcription helpers shared across UI and practice flow
 * Supports both direct OpenAI API and Cloudflare Worker proxy modes
 */

import { SPEECH_CONSTANTS, sanitizeOpenAiModel, sanitizeOpenAiVoice } from '@config/speech'
import { API_CONFIG, getApiBaseUrl, getProxyHeaders } from '@config/api'
import { storeTurnstileSessionFromResponse } from '@lib/turnstile'
import type { OpenAIModel, OpenAIVoice } from '../types'

/**
 * Custom error for rate limit exceeded
 */
export class RateLimitError extends Error {
  retryAfter: number // seconds until rate limit resets
  remaining: number
  resetTime: number // Unix timestamp

  constructor(message: string, retryAfter: number, remaining: number, resetTime: number) {
    super(message)
    this.name = 'RateLimitError'
    this.retryAfter = retryAfter
    this.remaining = remaining
    this.resetTime = resetTime
  }

  /**
   * Get human-readable time until reset
   */
  getTimeUntilReset(): string {
    // Use retryAfter if available, otherwise calculate from resetTime
    let seconds = this.retryAfter
    if (seconds <= 0 && this.resetTime > 0) {
      seconds = Math.max(0, this.resetTime - Math.floor(Date.now() / 1000))
    }

    const minutes = Math.ceil(seconds / 60)
    if (minutes <= 1) {
      return 'less than a minute'
    }
    return `${minutes} minute${minutes > 1 ? 's' : ''}`
  }
}

/**
 * Parse rate limit headers from response
 */
function parseRateLimitHeaders(response: Response): { retryAfter: number; remaining: number; resetTime: number } {
  const retryAfter = parseInt(response.headers.get('Retry-After') || '0', 10)
  const remaining = parseInt(response.headers.get('X-RateLimit-Remaining') || '0', 10)
  const resetTime = parseInt(response.headers.get('X-RateLimit-Reset') || '0', 10)
  return { retryAfter, remaining, resetTime }
}

interface TestApiKeyParams {
  apiKey?: string // Optional when using proxy
  voice: OpenAIVoice
  model: OpenAIModel
  timeoutMs?: number
  useProxy?: boolean
}

export async function testSpeechApiKey({
  apiKey,
  voice,
  model,
  timeoutMs = 8000,
  useProxy = false,
}: TestApiKeyParams): Promise<void> {
  // API key required only in direct mode
  if (!useProxy && !apiKey) {
    throw new Error('Missing API key')
  }

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  const baseUrl = getApiBaseUrl(useProxy ? 'proxy' : 'direct')
  const url = `${baseUrl}${API_CONFIG.TTS_PATH}`

  // Build headers based on mode
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (useProxy) {
    // Add timestamp for proxy validation
    Object.assign(headers, await getProxyHeaders())
  } else if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`
  }

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      signal: controller.signal,
      body: JSON.stringify({
        model: sanitizeOpenAiModel(model),
        voice: sanitizeOpenAiVoice(voice),
        input: 'RocketShip Readers voice check',
        format: 'mp3',
      }),
    })

    if (!response.ok) {
      if (response.status === 429) {
        const { retryAfter, remaining, resetTime } = parseRateLimitHeaders(response)
        throw new RateLimitError(
          `Rate limit exceeded. Try again in ${Math.ceil(retryAfter / 60)} minute(s).`,
          retryAfter,
          remaining,
          resetTime
        )
      }
      if (response.status === 403) {
        throw new Error('Request validation failed')
      }
      throw new Error(`Speech test failed (${response.status})`)
    }

    // We only need to confirm the request succeeds; drain the body to avoid leaks.
    storeTurnstileSessionFromResponse(response)
    await response.arrayBuffer()
  } finally {
    window.clearTimeout(timeout)
  }
}

export interface TranscriptionResult {
  text: string
}

interface TranscribeParams {
  blob: Blob
  apiKey?: string // Optional when using proxy
  prompt?: string
  timeoutMs?: number
  useProxy?: boolean
}

export async function transcribeWithOpenAiBlob({
  blob,
  apiKey,
  prompt,
  timeoutMs = 16000,
  useProxy = false,
}: TranscribeParams): Promise<TranscriptionResult> {
  if (!blob) {
    throw new Error('Missing audio')
  }

  // API key required only in direct mode
  if (!useProxy && !apiKey) {
    throw new Error('Missing API key')
  }

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs)

  const baseUrl = getApiBaseUrl(useProxy ? 'proxy' : 'direct')
  const url = `${baseUrl}${API_CONFIG.TRANSCRIPTION_PATH}`

  // Build headers based on mode
  const headers: Record<string, string> = {}

  if (useProxy) {
    // Add timestamp for proxy validation
    Object.assign(headers, await getProxyHeaders())
  } else if (apiKey) {
    headers['Authorization'] = `Bearer ${apiKey}`
  }

  try {
    const formData = new FormData()
    formData.append('file', blob, 'speech.webm')
    formData.append('model', SPEECH_CONSTANTS.TRANSCRIBE_MODEL)
    if (prompt) {
      formData.append('prompt', prompt)
    }
    formData.append('response_format', 'json')

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
      signal: controller.signal,
    })

    if (!response.ok) {
      if (response.status === 429) {
        const { retryAfter, remaining, resetTime } = parseRateLimitHeaders(response)
        throw new RateLimitError(
          `Rate limit exceeded. Try again in ${Math.ceil(retryAfter / 60)} minute(s).`,
          retryAfter,
          remaining,
          resetTime
        )
      }
      if (response.status === 403) {
        throw new Error('Request validation failed')
      }
      throw new Error(`Transcription failed (${response.status})`)
    }

    storeTurnstileSessionFromResponse(response)
    const data = (await response.json()) as { text?: string }
    return { text: data.text || '' }
  } finally {
    window.clearTimeout(timeout)
  }
}
